// ── Значения от типографии, В МИЛЛИМЕТРАХ ────────────────────────────────────
// Доли считаются от РАЗМЕРОВ КОНКРЕТНОГО ФОРМАТА (не хардкод 155/210).
export const PAGE_SPINE_MM = 18;          // корешок (у сгиба)
export const PAGE_SPINE_LARGE_MM = 18;    // 96 стр. — уточнить у типографии, пока = 18
export const PAGE_SPINE_THRESHOLD_PAGES = 96;

export const PAGE_BLEED_OUTER_X_MM = 5;   // внешний боковой край (напротив корешка)
export const PAGE_BLEED_Y_MM = 5;         // верх и низ

// Видимое белое поле для framed-макетов (листы с белыми краями), в мм.
// Одинаковое со всех сторон, считается от ВИДИМОГО размера страницы.
// Держать в синхроне с сервером: worker/generators.py (PAGE_WHITE_MM) и
// main/pdf_generator.py (FRAME_WHITE_MM).
export const FRAME_WHITE_MM = 10;

// Фолбэк-размеры, если формат ещё не загружен (чтобы не делить на 0/undefined).
const FALLBACK_W_MM = 155;
const FALLBACK_H_MM = 210;

// Корешок в мм по количеству страниц.
export function getPageSpineMm(pagesCount) {
  return (pagesCount && pagesCount >= PAGE_SPINE_THRESHOLD_PAGES)
    ? PAGE_SPINE_LARGE_MM
    : PAGE_SPINE_MM;
}

// Доля корешка от ширины страницы ДАННОГО формата (18/ширина_формата).
export function getPageSpineFraction(pagesCount, pageWidthMm) {
  const w = pageWidthMm || FALLBACK_W_MM;
  return getPageSpineMm(pagesCount) / w;
}

// Ширина ячейки макета в ДОЛЯХ ширины страницы (0..1) —
// та же flex-математика, что и в getSlotAspectRatio (PhotoBookEditor.jsx).
export function getSlotWidthFraction(layout, slotIndex) {
  if (!layout?.rows?.length) return 1;

  let si = 0;
  for (const row of layout.rows) {
    const rowTotalFlex = row.slots.reduce((s, sl) => s + (sl.flex ?? 1), 0);
    for (const slot of row.slots) {
      const slotFrac = (slot.flex ?? 1) / rowTotalFlex;
      if (slot.columnSlots) {
        for (let c = 0; c < slot.columnSlots.length; c++) {
          if (si === slotIndex) return slotFrac;
          si++;
        }
      } else {
        if (si === slotIndex) return slotFrac;
        si++;
      }
    }
  }
  return 1;
}

// Доля корешка от ширины КОНКРЕТНОЙ ЯЧЕЙКИ.
// Параметр pageWidthMm сохранён для совместимости существующих вызовов
// и НЕ используется: доля от ширины страницы не зависит от её миллиметров.
// Ограничение 0.6 — защита от вырожденно узких ячеек.
export function getSpineFraction(layout, slotIndex, pageWidthMm, pagesCount) {
  const slotFrac = getSlotWidthFraction(layout, slotIndex);
  if (!slotFrac || slotFrac <= 0) return 0;
  // Доля корешка от ЯЧЕЙКИ = (корешок / ширина формата) / (доля ширины ячейки).
  // Пример big-left: маленькая правая ячейка (доля ~0.42) → корешок 11.6%/0.42 ≈ 27.6%.
  return Math.min(getPageSpineFraction(pagesCount, pageWidthMm) / slotFrac, 0.6);
}


// ── Вылеты под обрез — доли от размеров ФОРМАТА ──────────────────────────────
export function getBleedXFraction(pageWidthMm) {
  return PAGE_BLEED_OUTER_X_MM / (pageWidthMm || FALLBACK_W_MM);   // 5/155 ≈ 0.032
}
export function getBleedYFraction(pageHeightMm) {
  return PAGE_BLEED_Y_MM / (pageHeightMm || FALLBACK_H_MM);        // 5/210 ≈ 0.024
}

// Скрываемые доли страницы для данной стороны разворота (по размерам формата).
// Левая страница: корешок 18мм справа, внешний бок 5мм слева; правая — зеркально.
// Верх/низ — по 5мм. Стороны НЕсимметричны (корешок толще внешнего края).
export function getPageHiddenFractions(side, pagesCount, pageWidthMm, pageHeightMm) {
  const spine = getPageSpineFraction(pagesCount, pageWidthMm);
  const outerX = getBleedXFraction(pageWidthMm);
  const bleedY = getBleedYFraction(pageHeightMm);
  const left  = side === "right" ? spine : outerX;   // корешок слева у правой стр.
  const right = side === "left"  ? spine : outerX;   // корешок справа у левой стр.
  return {
    left, right, top: bleedY, bottom: bleedY,
    visW: 1 - left - right,   // видимая доля ширины (18мм+5мм скрыто по X)
    visH: 1 - bleedY * 2,     // видимая доля высоты
  };
}

// Видимый размер страницы (мм) для aspectRatio разворотов.
// visW/visH одинаковы для левой и правой (суммы скрытых зон равны).
export function getVisiblePageSize(pageWidthMm, pageHeightMm, pagesCount) {
  const f = getPageHiddenFractions("left", pagesCount, pageWidthMm, pageHeightMm);
  return { w: (pageWidthMm || FALLBACK_W_MM) * f.visW, h: (pageHeightMm || FALLBACK_H_MM) * f.visH };
}

// Доля равномерного белого поля framed от ВИДИМОГО размера страницы.
// Возвращает { x, y } — доли для padding по горизонтали/вертикали.
// Обе доли считаем от видимой ШИРИНЫ, потому что CSS %-padding у всех сторон
// (в т.ч. pt/pb) берётся от ширины контейнера, а мм→px на экране одинаков по
// осям → одинаковая доля-от-ширины даёт ровное поле 10 мм со всех сторон.
export function getFrameWhiteFraction(pageWidthMm, pageHeightMm, pagesCount) {
  const vis = getVisiblePageSize(pageWidthMm, pageHeightMm, pagesCount);
  const f = FRAME_WHITE_MM / (vis.w || FALLBACK_W_MM);
  return { x: f, y: f };
}

// Рабочая зона framed-ячейки в мм: видимая страница минус белое поле со всех
// сторон. Нужна для честной пропорции ячейки в редакторе фото.
export function getFrameWorkSize(pageWidthMm, pageHeightMm, pagesCount) {
  const vis = getVisiblePageSize(pageWidthMm, pageHeightMm, pagesCount);
  return {
    w: Math.max(1, vis.w - 2 * FRAME_WHITE_MM),
    h: Math.max(1, vis.h - 2 * FRAME_WHITE_MM),
  };
}

// Поля framed относительно ПОЛНОЙ страницы. Вертикальные доли считаются
// от высоты: эти значения предназначены для absolute top/bottom, не padding.
// Размеры здесь сохраняют текущий контракт 18/5/10 мм; PDF-геометрия
// вводится отдельным согласованным этапом на сервере и фронтенде.
export function getFramePageInsets(side, pagesCount, pageWidthMm, pageHeightMm) {
  const w = pageWidthMm || FALLBACK_W_MM;
  const h = pageHeightMm || FALLBACK_H_MM;
  const hidden = getPageHiddenFractions(side, pagesCount, w, h);
  return {
    left: hidden.left + FRAME_WHITE_MM / w,
    right: hidden.right + FRAME_WHITE_MM / w,
    top: hidden.top + FRAME_WHITE_MM / h,
    bottom: hidden.bottom + FRAME_WHITE_MM / h,
  };
}

// Высота ячейки макета в ДОЛЯХ высоты страницы (0..1).
export function getSlotHeightFraction(layout, slotIndex) {
  if (!layout?.rows?.length) return 1;
  let si = 0;
  for (const row of layout.rows) {
    const rowFrac = (parseFloat(row.height) || 100) / 100;
    for (const slot of row.slots) {
      if (slot.columnSlots) {
        const colTotal = slot.columnSlots.reduce((s, c) => s + (c.flex ?? 1), 0);
        for (const col of slot.columnSlots) {
          if (si === slotIndex) return rowFrac * ((col.flex ?? 1) / colTotal);
          si++;
        }
      } else {
        if (si === slotIndex) return rowFrac;
        si++;
      }
    }
  }
  return 1;
}

// Желтые вылеты в долях КОНКРЕТНОЙ ячейки — для оверлеев в редакторе фото.
// Считаются от долей страницы той же логикой, что и корешок: чем меньше
// ячейка, тем толще полоса относительно неё.
export function getSlotBleedFractions(layout, slotIndex, pageWidthMm, pageHeightMm) {
  const wf = getSlotWidthFraction(layout, slotIndex) || 1;
  const hf = getSlotHeightFraction(layout, slotIndex) || 1;
  return {
    x: Math.min(getBleedXFraction(pageWidthMm) / wf, 0.3),
    y: Math.min(getBleedYFraction(pageHeightMm) / hf, 0.3),
  };
}